import { supabase } from '../config/supabase';
import { formatCurrency } from '../utils/currency';
import { accountsService } from './accountsService';
import { contactsService } from './contactsService';
import { purchaseOrdersService } from './purchaseOrdersService';

// Utility functions for atomic operations
const atomicUtils = {
  // Check if error is network-related
  isNetworkError: (error: any): boolean => {
    return error?.message?.includes('fetch') ||
           error?.message?.includes('network') ||
           error?.code === 'NETWORK_ERROR' ||
           !navigator.onLine;
  },

  // Retry function with exponential backoff
  retryWithBackoff: async <T>(
    operation: () => Promise<T>,
    maxRetries: number = 3,
    baseDelay: number = 1000
  ): Promise<T> => {
    let lastError: Error;

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error as Error;

        if (attempt === maxRetries || !atomicUtils.isNetworkError(error)) {
          throw error;
        }

        const delay = baseDelay * Math.pow(2, attempt);
        console.log(`Network error, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries + 1})`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }

    throw lastError!;
  },

  // Enhanced error logging
  logTransactionError: (operation: string, error: Error, transactionLog: string[]) => {
    const errorDetails = {
      operation,
      message: error.message,
      timestamp: new Date().toISOString(),
      isNetworkError: atomicUtils.isNetworkError(error),
      transactionLog: [...transactionLog]
    };

    console.error('Atomic transaction failed:', errorDetails);

    // In a production app, you might want to send this to an error reporting service
    // errorReportingService.captureException(error, { extra: errorDetails });
  },

  // Transaction logger for debugging
  createTransactionLogger: (operation: string) => {
    const logs: string[] = [];
    const startTime = Date.now();

    return {
      log: (message: string) => {
        const timestamp = new Date().toISOString();
        const logEntry = `[${timestamp}] ${message}`;
        logs.push(logEntry);
        console.log(`${operation}:`, message);
      },

      getLogs: () => [...logs],

      getDuration: () => Date.now() - startTime,

      logError: (error: Error) => {
        atomicUtils.logTransactionError(operation, error, logs);
      }
    };
  }
};

export interface Payment {
  id: string;
  business_id: string;
  invoice_id?: string;
  purchase_order_id?: string;
  account_id?: string;
  amount: number;
  payment_date: string;
  payment_method?: string;
  reference_number?: string;
  description?: string;
  attachment_url?: string;
  created_at: string;
  updated_at: string;
  // Relations
  account?: {
    id: string;
    name: string;
    type: string;
    balance: number;
  };
}

export interface PaymentFormData {
  invoice_id?: string;
  purchase_order_id?: string;
  account_id: string;
  amount: number;
  payment_date: string;
  payment_method?: string;
  reference_number?: string;
  description?: string;
  attachment_url?: string;
}

export const paymentsService = {
  // Get all payments for the business
  async getPayments(businessId?: string): Promise<{ payments: Payment[]; error: Error | null }> {
    try {
      let query = supabase
        .from('payments')
        .select(`
          *,
          account:accounts(id, name, type, balance)
        `)
        .order('created_at', { ascending: false });

      // Filter by business_id if provided
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { payments: data || [], error: null };
    } catch (error) {
      return { payments: [], error: error as Error };
    }
  },

  // Get payments by purchase order
  async getPaymentsByPurchaseOrder(purchaseOrderId: string, businessId?: string): Promise<{ payments: Payment[]; error: Error | null }> {
    try {
      let query = supabase
        .from('payments')
        .select(`
          *,
          account:accounts(id, name, type, balance)
        `)
        .eq('purchase_order_id', purchaseOrderId)
        .order('created_at', { ascending: false });

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;
      return { payments: data || [], error: null };
    } catch (error) {
      return { payments: [], error: error as Error };
    }
  },

  // Get payment by ID
  async getPaymentById(id: string, businessId?: string): Promise<{ payment: Payment | null; error: Error | null }> {
    try {
      let query = supabase
        .from('payments')
        .select(`
          *,
          account:accounts(id, name, type, balance)
        `)
        .eq('id', id);

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query.single();

      if (error) throw error;
      return { payment: data, error: null };
    } catch (error) {
      return { payment: null, error: error as Error };
    }
  },

  // Upload payment attachment
  async uploadPaymentAttachment(file: File, paymentId?: string, businessId?: string): Promise<string | undefined> {
    try {
      if (!businessId) {
        throw new Error('Business ID is required for file upload');
      }

      // Generate file path
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const fileName = paymentId ? `${paymentId}_${timestamp}.${fileExt}` : `temp_${timestamp}.${fileExt}`;
      const filePath = `${businessId}/payments/${fileName}`;

      // Upload file
      const { error } = await supabase.storage
        .from('payment-attachments')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false,
        });

      if (error) throw error;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('payment-attachments')
        .getPublicUrl(filePath);

      return publicUrl;
    } catch (error) {
      console.error('Error uploading payment attachment:', error);
      throw error;
    }
  },

  // Create a new payment with atomic operations
  async createPaymentAtomic(paymentData: PaymentFormData, businessId?: string): Promise<{ payment: Payment | null; error: Error | null }> {
    const logger = atomicUtils.createTransactionLogger('createPayment');
    const rollbackActions: (() => Promise<void>)[] = [];

    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      logger.log(`Starting atomic payment creation for amount: ${paymentData.amount}`);

      // Step 1: Create the payment record
      logger.log('Step 1: Creating payment record');
      const { data: payment, error: paymentError } = await atomicUtils.retryWithBackoff(async () => {
        const result = await supabase
          .from('payments')
          .insert([
            {
              business_id: businessId,
              invoice_id: paymentData.invoice_id,
              purchase_order_id: paymentData.purchase_order_id,
              account_id: paymentData.account_id,
              amount: paymentData.amount,
              payment_date: paymentData.payment_date,
              payment_method: paymentData.payment_method,
              reference_number: paymentData.reference_number,
              description: paymentData.description,
              attachment_url: paymentData.attachment_url,
            },
          ])
          .select(`
            *,
            account:accounts(id, name, type, balance)
          `)
          .single();

        if (result.error) throw result.error;
        if (!result.data) throw new Error('Failed to create payment - no data returned');
        return result;
      });

      if (paymentError) throw paymentError;
      if (!payment) throw new Error('Failed to create payment - no data returned');

      // Add rollback action for payment
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Deleting payment record');
        await supabase.from('payments').delete().eq('id', payment.id);
      });

      // Step 2: Update account balance (subtract payment amount)
      logger.log('Step 2: Updating account balance');
      const { account: currentAccount, error: accountError } = await accountsService.getAccountById(paymentData.account_id, businessId);

      if (accountError) throw accountError;
      if (!currentAccount) throw new Error('Account not found');

      const newAccountBalance = currentAccount.balance - paymentData.amount;
      const { error: accountBalanceError } = await accountsService.updateAccountBalance(paymentData.account_id, newAccountBalance);

      if (accountBalanceError) throw accountBalanceError;

      // Add rollback action for account balance
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Restoring account balance');
        await accountsService.updateAccountBalance(paymentData.account_id, currentAccount.balance);
      });

      // Step 3: Update vendor balance (subtract payment amount since it's payment to vendor)
      if (paymentData.purchase_order_id) {
        logger.log('Step 3: Updating vendor balance for purchase order payment');

        // Get purchase order to find vendor
        const { order: purchaseOrder, error: poError } = await purchaseOrdersService.getPurchaseOrderById(paymentData.purchase_order_id, businessId);

        if (poError) throw poError;
        if (!purchaseOrder) throw new Error('Purchase order not found');

        if (purchaseOrder.vendor_id) {
          const { contact: vendor, error: vendorError } = await contactsService.getContactById(purchaseOrder.vendor_id, businessId);

          if (vendorError) throw vendorError;
          if (!vendor) throw new Error('Vendor not found');

          const newVendorBalance = vendor.balance - paymentData.amount; // Subtract since it's payment to vendor
          const { error: vendorBalanceError } = await contactsService.updateContactBalance(purchaseOrder.vendor_id, newVendorBalance);

          if (vendorBalanceError) throw vendorBalanceError;

          // Add rollback action for vendor balance
          rollbackActions.push(async () => {
            logger.log('ROLLBACK: Restoring vendor balance');
            await contactsService.updateContactBalance(purchaseOrder.vendor_id, vendor.balance);
          });

          // Step 4: Update purchase order status if fully paid
          logger.log('Step 4: Checking and updating purchase order status');
          const { total: totalPaid, error: totalPaidError } = await this.getTotalPaymentsForPurchaseOrder(paymentData.purchase_order_id, businessId);

          if (totalPaidError) throw totalPaidError;

          const totalPaidAmount = (totalPaid || 0) + paymentData.amount;

          if (totalPaidAmount >= purchaseOrder.total_amount) {
            // Mark as paid
            const { error: statusError } = await purchaseOrdersService.updatePurchaseOrderStatus(paymentData.purchase_order_id, 'paid', businessId);
            if (statusError) throw statusError;

            // Add rollback action for status
            rollbackActions.push(async () => {
              logger.log('ROLLBACK: Restoring purchase order status');
              await purchaseOrdersService.updatePurchaseOrderStatus(paymentData.purchase_order_id!, purchaseOrder.status, businessId!);
            });
          }
        }
      }

      logger.log('Payment transaction completed successfully');
      return { payment, error: null };

    } catch (error) {
      const err = error as Error;
      logger.log(`Transaction failed: ${err.message}`);

      // Log detailed error information
      logger.logError(err);

      // Execute rollback actions in reverse order
      logger.log('Starting rollback process...');
      for (let i = rollbackActions.length - 1; i >= 0; i--) {
        try {
          await rollbackActions[i]();
        } catch (rollbackError) {
          const rbErr = rollbackError as Error;
          logger.log(`Rollback step ${i} failed: ${rbErr.message}`);
          console.warn(`Rollback step ${i} failed:`, rbErr);
        }
      }

      logger.log('Rollback completed');

      // Return user-friendly error message
      const isNetworkError = atomicUtils.isNetworkError(err);
      const userMessage = isNetworkError
        ? 'Network connection lost. The payment has been rolled back. Please check your connection and try again.'
        : 'Failed to create payment. All changes have been rolled back. Please try again.';

      return { payment: null, error: new Error(userMessage) };
    }
  },

  // Create a new payment (legacy method - now uses atomic version)
  async createPayment(paymentData: PaymentFormData, businessId?: string): Promise<{ payment: Payment | null; error: Error | null }> {
    return this.createPaymentAtomic(paymentData, businessId);
  },

  // Update an existing payment
  async updatePayment(id: string, paymentData: Partial<PaymentFormData>): Promise<{ payment: Payment | null; error: Error | null }> {
    try {
      const { data, error } = await supabase
        .from('payments')
        .update({
          account_id: paymentData.account_id,
          amount: paymentData.amount,
          payment_date: paymentData.payment_date,
          payment_method: paymentData.payment_method,
          reference_number: paymentData.reference_number,
          description: paymentData.description,
          attachment_url: paymentData.attachment_url,
        })
        .eq('id', id)
        .select(`
          *,
          account:accounts(id, name, type, balance)
        `)
        .single();

      if (error) throw error;
      return { payment: data, error: null };
    } catch (error) {
      return { payment: null, error: error as Error };
    }
  },

  // Delete a payment with atomic operations
  async deletePaymentAtomic(id: string, businessId?: string): Promise<{ error: Error | null }> {
    const logger = atomicUtils.createTransactionLogger('deletePayment');
    const rollbackActions: (() => Promise<void>)[] = [];

    try {
      if (!businessId) {
        throw new Error('Business ID is required');
      }

      logger.log(`Starting atomic payment deletion for ID: ${id}`);

      // Step 1: Get the payment data before deletion
      logger.log('Step 1: Fetching payment data');
      const { payment, error: fetchError } = await this.getPaymentById(id, businessId);

      if (fetchError) throw fetchError;
      if (!payment) throw new Error('Payment not found');

      // Step 2: Restore account balance (add back the payment amount)
      logger.log('Step 2: Restoring account balance');
      const { account: currentAccount, error: accountError } = await accountsService.getAccountById(payment.account_id!, businessId);

      if (accountError) throw accountError;
      if (!currentAccount) throw new Error('Account not found');

      const restoredAccountBalance = currentAccount.balance + payment.amount;
      const { error: accountBalanceError } = await accountsService.updateAccountBalance(payment.account_id!, restoredAccountBalance);

      if (accountBalanceError) throw accountBalanceError;

      // Add rollback action for account balance
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Reverting account balance restoration');
        await accountsService.updateAccountBalance(payment.account_id!, currentAccount.balance);
      });

      // Step 3: Restore vendor balance (add back the payment amount since it was subtracted)
      if (payment.purchase_order_id) {
        logger.log('Step 3: Restoring vendor balance for purchase order payment');

        // Get purchase order to find vendor
        const { order: purchaseOrder, error: poError } = await purchaseOrdersService.getPurchaseOrderById(payment.purchase_order_id, businessId);

        if (poError) throw poError;
        if (!purchaseOrder) throw new Error('Purchase order not found');

        if (purchaseOrder.vendor_id) {
          const { contact: vendor, error: vendorError } = await contactsService.getContactById(purchaseOrder.vendor_id, businessId);

          if (vendorError) throw vendorError;
          if (!vendor) throw new Error('Vendor not found');

          const restoredVendorBalance = vendor.balance + payment.amount; // Add back since it was subtracted
          const { error: vendorBalanceError } = await contactsService.updateContactBalance(purchaseOrder.vendor_id, restoredVendorBalance);

          if (vendorBalanceError) throw vendorBalanceError;

          // Add rollback action for vendor balance
          rollbackActions.push(async () => {
            logger.log('ROLLBACK: Reverting vendor balance restoration');
            await contactsService.updateContactBalance(purchaseOrder.vendor_id, vendor.balance);
          });

          // Step 4: Check if purchase order should be marked as unpaid
          logger.log('Step 4: Checking and updating purchase order status');
          const { total: totalPaid, error: totalPaidError } = await this.getTotalPaymentsForPurchaseOrder(payment.purchase_order_id, businessId);

          if (totalPaidError) throw totalPaidError;

          const remainingPaidAmount = (totalPaid || 0) - payment.amount;

          if (remainingPaidAmount < purchaseOrder.total_amount) {
            // Mark as unpaid if no longer fully paid
            const { error: statusError } = await purchaseOrdersService.updatePurchaseOrderStatus(payment.purchase_order_id, 'unpaid', businessId);
            if (statusError) throw statusError;

            // Add rollback action for status
            rollbackActions.push(async () => {
              logger.log('ROLLBACK: Restoring purchase order status');
              await purchaseOrdersService.updatePurchaseOrderStatus(payment.purchase_order_id!, purchaseOrder.status, businessId!);
            });
          }
        }
      }

      // Step 5: Delete the payment record
      logger.log('Step 5: Deleting payment record');
      const { error: deleteError } = await supabase
        .from('payments')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;

      // Add rollback action for payment (this would be complex to restore, so we'll log it)
      rollbackActions.push(async () => {
        logger.log('ROLLBACK: Note - payment record was deleted and cannot be automatically restored');
      });

      logger.log('Payment deletion transaction completed successfully');
      return { error: null };

    } catch (error) {
      const err = error as Error;
      logger.log(`Transaction failed: ${err.message}`);

      // Log detailed error information
      logger.logError(err);

      // Execute rollback actions in reverse order
      logger.log('Starting rollback process...');
      for (let i = rollbackActions.length - 1; i >= 0; i--) {
        try {
          await rollbackActions[i]();
        } catch (rollbackError) {
          const rbErr = rollbackError as Error;
          logger.log(`Rollback step ${i} failed: ${rbErr.message}`);
          console.warn(`Rollback step ${i} failed:`, rbErr);
        }
      }

      logger.log('Rollback completed');

      // Return user-friendly error message
      const isNetworkError = atomicUtils.isNetworkError(err);
      const userMessage = isNetworkError
        ? 'Network connection lost. The payment deletion has been rolled back. Please check your connection and try again.'
        : 'Failed to delete payment. All changes have been rolled back. Please try again.';

      return { error: new Error(userMessage) };
    }
  },

  // Delete a payment (legacy method - now uses atomic version)
  async deletePayment(id: string, businessId?: string): Promise<{ error: Error | null }> {
    if (!businessId) {
      return { error: new Error('Business ID is required for atomic payment deletion') };
    }
    return this.deletePaymentAtomic(id, businessId);
  },

  // Get total payments for a purchase order
  async getTotalPaymentsForPurchaseOrder(purchaseOrderId: string, businessId?: string): Promise<{ total: number; error: Error | null }> {
    try {
      let query = supabase
        .from('payments')
        .select('amount')
        .eq('purchase_order_id', purchaseOrderId);

      // Filter by business_id if provided for security
      if (businessId) {
        query = query.eq('business_id', businessId);
      }

      const { data, error } = await query;

      if (error) throw error;

      const total = data?.reduce((sum, payment) => sum + payment.amount, 0) || 0;
      return { total, error: null };
    } catch (error) {
      return { total: 0, error: error as Error };
    }
  },

  // Get formatted payment amount
  getFormattedAmount(amount: number): string {
    return formatCurrency(amount);
  },

  // Get payment method label
  getPaymentMethodLabel(method: string): string {
    const labels: Record<string, string> = {
      cash: 'Cash',
      bank_transfer: 'Bank Transfer',
      credit_card: 'Credit Card',
      check: 'Check',
      other: 'Other',
    };
    return labels[method] || method;
  },
};